import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { AxiosResponse } from "axios";

// Import types from server schema - SINGLE SOURCE OF TRUTH
import type {
  AcknowledgeMailRequest,
  AcknowledgeMailResponse,
  GetMailRequest,
  GetMailResponse,
  GetMailsRequest,
  GetMailsResponse,
  GetMailThreadRequest,
  GetMailThreadResponse,
  DeleteThreadRequest,
  DeleteThreadResponse,
  UpdateTagRequest,
  UpdateTagResponse,
  SendMailRequest,
  SendMailResponse,
  AddVisibilityUsersRequest,
  AddVisibilityUsersResponse,
  RemoveVisibilityUsersRequest,
  RemoveVisibilityUsersResponse,
  UpdateMailCustomFieldsRequest,
  UpdateMailCustomFieldsResponse,
  ExportMailsToExcelResponse,
  ExportMailsToExcelRequest,
  InheritMailCustomFieldsRequest,
  InheritMailCustomFieldsResponse,
} from "@ServerTypes/mails/mails.schema";
import type {
  AddProjectWhitelistEmailsRequest,
  AddProjectWhitelistEmailsResponse,
  RemoveProjectWhitelistEmailsRequest,
  RemoveProjectWhitelistEmailsResponse,
  UpdateMailboxWhitelistStatusRequest,
  UpdateMailboxWhitelistStatusResponse,
} from "@ServerTypes/projects/project.schema";

/** Mailbox grid list endpoint — owned by the mailbox feature (used as SlickGrid remoteDataSource). */
export const mailsUrl = `${getBackendUrl()}/mailbox/getMails`;

/**
 * Get mail thread by parent ID.
 * Types are derived from server schema (single source of truth).
 */
export const getMailThreadRequest = (data: GetMailThreadRequest): Promise<AxiosResponse<GetMailThreadResponse>> =>
  axiosWrapper.post<GetMailThreadResponse, GetMailThreadRequest>(`${getBackendUrl()}/mailbox/getMailThread`, data);

/**
 * Acknowledge a mail - marks it as acknowledged by the current user.
 * Types are derived from server schema (single source of truth).
 *
 * @param data - Request payload with mailId (24-char MongoDB ObjectId)
 * @returns Promise with AxiosResponse containing status and optional message
 */
export const acknowledgeMailRequest = (data: AcknowledgeMailRequest): Promise<AxiosResponse<AcknowledgeMailResponse>> =>
  axiosWrapper.post<AcknowledgeMailResponse, AcknowledgeMailRequest>(
    `${getBackendUrl()}/mailbox/acknowledgeMail`,
    data,
  );

/**
 * Get a single mail by ID.
 * Types are derived from server schema (single source of truth).
 */
export const getMailRequest = (data: GetMailRequest): Promise<AxiosResponse<GetMailResponse>> =>
  axiosWrapper.post<GetMailResponse, GetMailRequest>(`${getBackendUrl()}/mailbox/getMail`, data);

/**
 * Add users to mail visibility.
 * Types are derived from server schema (single source of truth).
 */
export const addVisibilityUsersRequest = (
  data: AddVisibilityUsersRequest,
): Promise<AxiosResponse<AddVisibilityUsersResponse>> =>
  axiosWrapper.post<AddVisibilityUsersResponse, AddVisibilityUsersRequest>(
    `${getBackendUrl()}/mailbox/addVisibilityUsers`,
    data,
  );

/**
 * Remove users from mail visibility.
 * Types are derived from server schema (single source of truth).
 */
export const removeVisibilityUsersRequest = (
  data: RemoveVisibilityUsersRequest,
): Promise<AxiosResponse<RemoveVisibilityUsersResponse>> =>
  axiosWrapper.post<RemoveVisibilityUsersResponse, RemoveVisibilityUsersRequest>(
    `${getBackendUrl()}/mailbox/removeVisibilityUsers`,
    data,
  );

/**
 * Update mail custom fields.
 * Types are derived from server schema (single source of truth).
 */
export const updateMailCustomFieldsRequest = (
  data: UpdateMailCustomFieldsRequest,
): Promise<AxiosResponse<UpdateMailCustomFieldsResponse>> =>
  axiosWrapper.post<UpdateMailCustomFieldsResponse, UpdateMailCustomFieldsRequest>(
    `${getBackendUrl()}/mailbox/updateMailCustomFields`,
    data,
  );

/**
 * Update mail tags.
 * Types are derived from server schema (single source of truth).
 */
export const updateMailTagRequest = (data: UpdateTagRequest): Promise<AxiosResponse<UpdateTagResponse>> =>
  axiosWrapper.post<UpdateTagResponse, UpdateTagRequest>(`${getBackendUrl()}/mailbox/updateTag`, data);

/**
 * Delete mail thread(s).
 * Types are derived from server schema (single source of truth).
 */
export const deleteThreadRequest = (data: DeleteThreadRequest): Promise<AxiosResponse<DeleteThreadResponse>> =>
  axiosWrapper.post<DeleteThreadResponse, DeleteThreadRequest>(`${getBackendUrl()}/mailbox/deleteThread`, data);

/**
 * Send a new mail or reply.
 * Types are derived from server schema (single source of truth).
 */
export const sendMailRequest = (data: SendMailRequest): Promise<AxiosResponse<SendMailResponse>> =>
  axiosWrapper.post<SendMailResponse, SendMailRequest>(`${getBackendUrl()}/mailbox/sendMail`, data);

export const inheritMailCustomFields = (
  data: InheritMailCustomFieldsRequest,
): Promise<AxiosResponse<InheritMailCustomFieldsResponse>> =>
  axiosWrapper.post<InheritMailCustomFieldsResponse, InheritMailCustomFieldsRequest>(
    `${getBackendUrl()}/mailbox/inheritMailCustomFields`,
    data,
  );

export const getMailsRequest = (data: GetMailsRequest) =>
  axiosWrapper.post<GetMailsResponse, GetMailsRequest>(`${getBackendUrl()}/mailbox/getMails`, data);

export const exportMailsToExcelRequest = (
  data: ExportMailsToExcelRequest,
): Promise<AxiosResponse<ExportMailsToExcelResponse>> =>
  axiosWrapper.post<ExportMailsToExcelResponse, ExportMailsToExcelRequest>(
    `${getBackendUrl()}/mailbox/exportMailsToExcel`,
    data,
  );

export const addProjectWhitelistEmailsRequest = (data: AddProjectWhitelistEmailsRequest) =>
  axiosWrapper.post<AddProjectWhitelistEmailsResponse>(`${getBackendUrl()}/projects/addProjectWhitelistEmails`, data);

export const removeProjectWhitelistEmailsRequest = (data: RemoveProjectWhitelistEmailsRequest) =>
  axiosWrapper.post<RemoveProjectWhitelistEmailsResponse>(
    `${getBackendUrl()}/projects/removeProjectWhitelistEmails`,
    data,
  );

export const updateMailboxWhitelistStatusRequest = (data: UpdateMailboxWhitelistStatusRequest) =>
  axiosWrapper.post<UpdateMailboxWhitelistStatusResponse>(
    `${getBackendUrl()}/projects/updateMailboxWhitelistStatus`,
    data,
  );
